/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  ScheduleSubmissionCompactUserModel,
  ProjectResearchSubjectBlockModel,
  ScheduleSubmissionBlockModel,
  ScheduleSubmissionTeacherSelectionModel,
  ScheduleSubmissionsTableModel,
  ScheduleSubmissionVersionSnapshot,
  ScheduleSubmissionVersionSnapshotProjectResearchSubject,
  ScheduleSubmissionVersionSnapshotTimetableBlock,
  SubjectScheduleBlockModel,
} from 'src/app/services/models/schedule/schedule-submissions.model';

type SnapshotRecord = Record<string, unknown>;

export function scheduleVersionSnapshotToSubmission(
  snapshot: ScheduleSubmissionVersionSnapshot,
  fallbackSubmission: ScheduleSubmissionsTableModel,
): ScheduleSubmissionsTableModel {
  const blocks = snapshotBlocks(snapshot);
  const snapshotSubmission = recordValue(snapshot.schedule_submission);

  return {
    ...fallbackSubmission,
    ...(snapshotSubmission as Partial<ScheduleSubmissionsTableModel>),
    academic_term: {
      ...fallbackSubmission.academic_term,
      ...(recordValue(snapshot.academic_term) as Partial<ScheduleSubmissionsTableModel['academic_term']>),
    },
    teacher: {
      ...fallbackSubmission.teacher,
      ...(recordValue(snapshot.teacher) as Partial<ScheduleSubmissionsTableModel['teacher']>),
    },
    approval_status: snapshot.approval_status ?? fallbackSubmission.approval_status,
    blocks,
    counts: {
      subject_schedules: blocks.filter((block) => block.type === 'subject_schedule').length,
      project_research_subjects: blocks.filter((block) => block.type === 'project_research_subject').length,
    },
  };
}

export function snapshotBlocks(snapshot: ScheduleSubmissionVersionSnapshot): ScheduleSubmissionBlockModel[] {
  const timetableBlocks = arrayValue<ScheduleSubmissionVersionSnapshotTimetableBlock>(
    snapshot.timetable?.blocks
  )
    .map((block) => toScheduleBlock(block))
    .filter((block): block is ScheduleSubmissionBlockModel => block != null);
  const existingProjectIds = new Set(
    timetableBlocks
      .filter((block): block is ProjectResearchSubjectBlockModel => block.type === 'project_research_subject')
      .map((block) => block.project_research_subject_assignment_id)
  );
  const projectBlocks = arrayValue<ScheduleSubmissionVersionSnapshotProjectResearchSubject>(
    snapshot.project_research_subjects
  )
    .map((block) => toProjectResearchBlock(block))
    .filter((block): block is ProjectResearchSubjectBlockModel => (
      block != null && !existingProjectIds.has(block.project_research_subject_assignment_id)
    ));

  return [...timetableBlocks, ...projectBlocks];
}

function toScheduleBlock(value: ScheduleSubmissionVersionSnapshotTimetableBlock): ScheduleSubmissionBlockModel | null {
  const type = stringValue(value.type);

  if (type === 'subject_schedule') {
    return toSubjectScheduleBlock(value);
  }

  if (type === 'project_research_subject') {
    return toProjectResearchBlock(value);
  }

  return null;
}

function toSubjectScheduleBlock(block: ScheduleSubmissionVersionSnapshotTimetableBlock): SubjectScheduleBlockModel {
  const subject = block.subject ?? {};
  const time = block.time ?? {};
  const teachingType = time.teaching_type ?? {};
  const mappedBlock: SubjectScheduleBlockModel & {
    teacher_selections?: ScheduleSubmissionTeacherSelectionModel[];
  } = {
    id: stringValue(block.id) || `snapshot-subject-schedule:${numberValue(block.subject_schedule_id)}`,
    type: 'subject_schedule',
    subject_schedule_group_id: numberValue(block.subject_schedule_group_id),
    subject_schedule_id: numberValue(block.subject_schedule_id),
    schedule_submission_subject_schedule_id: numberValue(block.schedule_submission_subject_schedule_id),
    subject: {
      id: numberValue(subject.id),
      code: stringValue(subject.code),
      name: stringValue(subject.name),
      courseunit: stringValue(subject.courseunit),
      credit: numberValue(subject.credit),
    },
    section: stringValue(block.section),
    student_count: nullableNumber(block.student_count),
    credit: nullableNumber(block.credit),
    curriculum_type: curriculumTypeValue(block.curriculum_type),
    time: {
      day_of_week: numberValue(time.day_of_week),
      start_time: stringValue(time.start_time),
      end_time: stringValue(time.end_time),
      room_name: nullableString(time.room_name),
      is_workload_split: booleanValue(time.is_workload_split),
      teaching_hours: numberValue(time.teaching_hours),
      workload_cap_hours: nullableNumber(time.workload_cap_hours),
      teaching_type: {
        id: numberValue(teachingType.id),
        code: stringValue(teachingType.code),
        name_th: stringValue(teachingType.name_th),
        name_en: stringValue(teachingType.name_en),
      },
    },
    status: stringValue(block.status),
    assignment_approval: approvalValue(block.assignment_approval),
    approval: approvalValue(block.approval ?? block.assignment_approval),
    comment: nullableString(block.comment),
    revision: stringValue(block.revision),
    counts: {
      selected_teaching_dates: numberValue(block.counts?.selected_teaching_dates),
    },
    teacher_selections: arrayValue(block.teacher_selections).map((selection) => (
      teacherSelectionValue(selection)
    )),
  };

  return mappedBlock;
}

function toProjectResearchBlock(
  block: ScheduleSubmissionVersionSnapshotProjectResearchSubject | ScheduleSubmissionVersionSnapshotTimetableBlock,
): ProjectResearchSubjectBlockModel | null {
  if (block == null) {
    return null;
  }
  const subject = block.subject ?? {};
  const fallbackId = block.id;
  const assignmentId = numberValue(
    block.project_research_subject_assignment_id ?? block.assignment_id ?? fallbackId
  );
  const recordId = numberValue(
    block.project_research_subject_id ?? block.project_research_subject_record_id ?? fallbackId
  );

  if (assignmentId <= 0 && recordId <= 0) {
    return null;
  }

  return {
    type: 'project_research_subject',
    project_research_subject_id: recordId,
    project_research_subject_assignment_id: assignmentId,
    subject: {
      id: numberValue(subject.id),
      code: stringValue(subject.code ?? block.course_code),
      name: stringValue(subject.name ?? block.course_name),
      courseunit: stringValue(subject.courseunit ?? block.courseunit),
      credit: numberValue(subject.credit ?? block.credit),
    },
    section: stringValue(block.section),
    curriculum_type: curriculumTypeValue(block.curriculum_type),
    status: stringValue(block.status),
    approval: approvalValue(block.approval),
    comment: nullableString(block.comment),
    description: stringValue(block.description),
    primary_advisor_student_count: numberValue(block.primary_advisor_student_count),
    co_advisor_student_count: numberValue(block.co_advisor_student_count),
    show_in_timetable: booleanValue(block.show_in_timetable),
    display_area: stringValue(block.display_area) || (booleanValue(block.show_in_timetable) ? 'timetable' : 'below_timetable'),
    display_label: stringValue(block.display_label),
    teaching_times: arrayValue(block.teaching_times).map((time) => {
      const timeRecord = recordValue(time);

      return {
        id: numberValue(timeRecord['id']),
        day_of_week: numberValue(timeRecord['day_of_week']),
        start_time: stringValue(timeRecord['start_time']),
        end_time: stringValue(timeRecord['end_time']),
        teaching_hours: stringValue(timeRecord['teaching_hours']),
      };
    }),
    counts: {
      teaching_times: numberValue(block.counts?.teaching_times ?? arrayValue(block.teaching_times).length),
    },
  };
}

function approvalValue(value: unknown): SubjectScheduleBlockModel['approval'] {
  const approval = recordValue(value);

  if (Object.keys(approval).length === 0) {
    return null;
  }

  const currentStatus = recordValue(approval.current_status);

  return {
    id: numberValue(approval.id),
    approvable_type: nullableString(approval.approvable_type),
    approvable_id: nullableNumber(approval.approvable_id),
    status_code: nullableString(approval.status_code),
    status_label: nullableString(approval.status_label),
    current_status: Object.keys(currentStatus).length === 0
      ? null
      : {
        id: nullableNumber(currentStatus.id),
        code: nullableString(currentStatus.code),
        name_th: nullableString(currentStatus.name_th),
        name_en: nullableString(currentStatus.name_en),
      },
    active_blockers: [],
  };
}

function teacherSelectionValue(value: unknown): ScheduleSubmissionTeacherSelectionModel {
  const selection = recordValue(value);

  return {
    schedule_submission_id: nullableNumber(selection.schedule_submission_id),
    schedule_submission_subject_schedule_id: nullableNumber(selection.schedule_submission_subject_schedule_id),
    user_id: nullableNumber(selection.user_id),
    user: compactUserValue(selection.user),
    status: nullableString(selection.status),
    approval: approvalValue(selection.approval),
    comment: nullableString(selection.comment),
    teaching_hours: nullableString(selection.teaching_hours),
    selected_teaching_dates: arrayValue(selection.selected_teaching_dates).map((date) => {
      const dateRecord = recordValue(date);

      return {
        id: nullableNumber(dateRecord.id),
        date: nullableString(dateRecord.date ?? dateRecord.teaching_date),
        subject_schedule_id: nullableNumber(dateRecord.subject_schedule_id),
        sequence_day: nullableString(dateRecord.sequence_day),
      };
    }),
  };
}

function curriculumTypeValue(value: unknown): SubjectScheduleBlockModel['curriculum_type'] {
  const curriculumType = recordValue(value);

  return {
    id: numberValue(curriculumType.id),
    level_id: numberValue(curriculumType.level_id),
    name: stringValue(curriculumType.name),
  };
}

function recordValue(value: unknown): SnapshotRecord {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as SnapshotRecord
    : {};
}

function arrayValue<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function stringValue(value: unknown): string {
  return primitiveString(value) ?? '';
}

function nullableString(value: unknown): string | null {
  return primitiveString(value);
}

function numberValue(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function nullableNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function booleanValue(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

function primitiveString(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }

  return null;
}

function compactUserValue(value: unknown): ScheduleSubmissionCompactUserModel | null {
  const user = recordValue(value);

  if (Object.keys(user).length === 0) {
    return null;
  }

  return {
    id: nullableNumber(user.id),
    full_name: nullableString(user.full_name),
    email: nullableString(user.email),
    department: recordValue(user.department),
  };
}
